import { Check } from 'lucide-react'
import { cn } from '@/lib/cn'
import type { ClientActivationState } from '@/lib/hooks/useMyActivationState'

interface Day0HorizontalStepperProps {
  stages: ClientActivationState['stages']
}

export function Day0HorizontalStepper({ stages }: Day0HorizontalStepperProps) {
  const total = stages.length

  return (
    <ol role="list" className="flex items-start gap-0 w-full">
      {stages.map((stage, i) => {
        // Drive from stage.state, not from position.
        // Position-based inference (i < activeIndex) gets non-monotonic states wrong:
        // a BLOCKED stage before the NEEDED stage would render as a green checkmark.
        const isDone = stage.state === 'READY'
        const isActive = stage.state === 'NEEDED'
        const label = stage.label

        return (
          <li
            key={stage.key}
            className="flex-1 flex flex-col items-center"
            aria-current={isActive ? 'step' : undefined}
            aria-label={`${label}, step ${i + 1} of ${total}${isDone ? ', completed' : isActive ? ', current' : ', upcoming'}`}
          >
            {/* Track line + circle row */}
            <div className="flex items-center w-full">
              {/* Left connector */}
              <div
                className={cn(
                  'flex-1 h-px',
                  i === 0 ? 'invisible' : isDone || isActive ? 'bg-primary' : 'bg-border',
                )}
                aria-hidden
              />

              {/* Step circle */}
              <div
                className={cn(
                  'shrink-0 flex items-center justify-center rounded-full size-8 text-sm font-semibold border-2 transition-colors',
                  isDone
                    ? 'bg-primary border-primary text-white'
                    : isActive
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-surface border-border text-text-muted',
                )}
                aria-hidden
              >
                {isDone ? <Check size={14} strokeWidth={2.5} /> : i + 1}
              </div>

              {/* Right connector */}
              <div
                className={cn(
                  'flex-1 h-px',
                  i === stages.length - 1 ? 'invisible' : isDone ? 'bg-primary' : 'bg-border',
                )}
                aria-hidden
              />
            </div>

            {/* Step label */}
            <span
              className={cn(
                'mt-2 text-center text-xs leading-tight px-1',
                isActive ? 'text-primary font-semibold' : isDone ? 'text-text-muted' : 'text-text-subtle',
              )}
              aria-hidden
            >
              {label}
            </span>
          </li>
        )
      })}
    </ol>
  )
}
